/**
 * Minimal REST wrappers for Google Drive v3 and Sheets v4 using fetch.
 * No gapi, no Google Picker, CSP-friendly.
 */

const SHEETS_BASE = "https://sheets.googleapis.com/v4/spreadsheets";
const DRIVE_BASE = "https://www.googleapis.com/drive/v3";

/**
 * Internal helper to GET JSON with Bearer token
 */
async function doGet(url, token) {
  const res = await fetch(url, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Google API ${res.status}: ${text}`);
  }
  return res.json();
}

/**
 * List spreadsheets owned/accessible by the user
 * @param {string} token OAuth 2.0 access token
 * @param {{ q?: string, pageToken?: string, pageSize?: number }} opts
 * @returns {Promise<{ files: Array, nextPageToken?: string }>}
 */
export async function listSpreadsheets(token, opts = {}) {
  const { q, pageToken, pageSize = 50 } = opts;
  const queryParts = [
    "mimeType='application/vnd.google-apps.spreadsheet'",
    // Optional name search
    q ? `name contains '${q.replace(/'/g, "\\'")}'` : null,
    // "trashed = false" // optionally filter trashed files
  ].filter(Boolean);
  const query = queryParts.join(" and ");

  const params = new URLSearchParams({
    q: query,
    fields: "nextPageToken,files(id,name,modifiedTime,owners(displayName))",
    orderBy: "modifiedTime desc",
    pageSize: String(pageSize),
  });
  if (pageToken) params.set("pageToken", pageToken);

  return doGet(`${DRIVE_BASE}/files?${params.toString()}`, token);
}

/**
 * Get spreadsheet title
 * @param {string} sheetId
 * @param {string} token
 * @returns {Promise<string>}
 */
export async function getSpreadsheetTitle(sheetId, token) {
  const data = await doGet(`${SHEETS_BASE}/${sheetId}`, token);
  return data?.properties?.title || "";
}

/**
 * Get tab (sheet) names
 * @param {string} sheetId
 * @param {string} token
 * @returns {Promise<string[]>}
 */
export async function getTabNames(sheetId, token) {
  const data = await doGet(`${SHEETS_BASE}/${sheetId}`, token);
  return (data?.sheets || []).map((s) => s?.properties?.title).filter(Boolean);
}

/**
 * Get cell values for a given A1 range
 * @param {string} sheetId
 * @param {string} range A1 notation (e.g. "Sheet1!A:A" or "Sheet1")
 * @param {string} token
 * @returns {Promise<string[][]>}
 */
export async function getValues(sheetId, range, token) {
  const encRange = encodeURIComponent(range);
  const data = await doGet(
    `${SHEETS_BASE}/${sheetId}/values/${encRange}`,
    token
  );
  return data?.values || [];
}
